/**
 * MapLegend — interactive accordion filter panel with glassmorphism.
 *
 * Six collapsible sections (Axis, Initiative, Status, Phase, Source, Relationships)
 * with chip toggles, All/None buttons, and active count badges.
 * Writes to the Zustand NodeFilters store.
 */

import { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import type {
  StrategicInitiative,
  Status,
  Phase,
  DataSource,
  AdjacencyAxis,
  RelationshipType,
} from '../../types/adjacency';
import { INITIATIVE_COLORS, STATUS_COLORS, STATUS_DEFAULT_COLOR } from '../../utils/map-colors';
import { AXIS_COLORS, PHASE_COLORS, SOURCE_COLORS, SOURCE_LABELS } from '../../utils/view-colors';
import { getAllPhases, getAllStatuses } from '../../utils/reference-data';
import { FilterChip } from './FilterChip';

// ── Value arrays ─────────────────────────────────────────────────────

const ALL_INITIATIVES: StrategicInitiative[] = [
  'US Mining', 'Proximity Detection', 'Analytics', 'Maritime',
  'International Mining', 'New Verticals', 'Capabilities', 'Other',
];


const ALL_AXES: AdjacencyAxis[] = [
  'Core', 'New Services', 'New Markets & Customers', 'Forward Integration',
  'New Technologies & Capabilities', 'New Channels', 'New Geographies',
  'Backward Integration', 'New Products',
];

const ALL_SOURCES: DataSource[] = ['priority-list', 'adjacency-map', 'manual'];

const ALL_REL_TYPES: RelationshipType[] = ['parent-child', 'related'];

const REL_TYPE_LABELS: Record<RelationshipType, string> = {
  'parent-child': 'Parent-Child',
  'related': 'Related',
};

const REL_TYPE_COLORS: Record<RelationshipType, string> = {
  'parent-child': '#3b82f6',
  'related': '#f59e0b',
};

// ── Component ────────────────────────────────────────────────────────

export function MapLegend() {
  const { filters, setFilters, resetFilters, nodes } = useAdjacencyStore();

  // Dynamic Phase/Status options — includes any SP-introduced values
  const allStatuses = useMemo(() => getAllStatuses(nodes), [nodes]);
  const allPhases = useMemo(() => getAllPhases(nodes), [nodes]);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    axis: false,
    initiative: true,
    status: false,
    phase: false,
    source: false,
    relationship: false,
  });

  const toggleSection = useCallback((key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Check if any filters are active
  const hasActiveFilters =
    filters.axes.length > 0 ||
    filters.initiatives.length > 0 ||
    filters.statuses.length > 0 ||
    filters.phases.length > 0 ||
    filters.sources.length > 0 ||
    filters.relationshipFilter.length > 0;

  return (
    <div className="glass-panel text-xs max-w-72 max-h-[70vh] overflow-y-auto">
      {/* ── Axis ─────────────────────────────────────── */}
      <FilterSection
        title="Axis"
        sectionKey="axis"
        isOpen={openSections.axis}
        onToggle={toggleSection}
        activeCount={filters.axes.length === 0 ? ALL_AXES.length : filters.axes.length}
        totalCount={ALL_AXES.length}
        onAll={() => setFilters({ axes: [] })}
        onNone={() => setFilters({ axes: ['__none__' as AdjacencyAxis] })}
      >
        <div className="flex flex-wrap gap-1">
          {ALL_AXES.map((axis) => (
            <FilterChip
              key={axis}
              label={axis}
              color={AXIS_COLORS[axis]}
              isActive={filters.axes.length === 0 || filters.axes.includes(axis)}
              onToggle={() => toggleFilter('axes', axis, ALL_AXES, filters.axes, setFilters)}
            />
          ))}
        </div>
      </FilterSection>

      {/* ── Strategic Initiative ─────────────────────── */}
      <FilterSection
        title="Strategic Initiative"
        sectionKey="initiative"
        isOpen={openSections.initiative}
        onToggle={toggleSection}
        activeCount={filters.initiatives.length === 0 ? ALL_INITIATIVES.length : filters.initiatives.length}
        totalCount={ALL_INITIATIVES.length}
        onAll={() => setFilters({ initiatives: [] })}
        onNone={() => setFilters({ initiatives: ['__none__' as StrategicInitiative] })}
      >
        <div className="flex flex-wrap gap-1">
          {ALL_INITIATIVES.map((init) => (
            <FilterChip
              key={init}
              label={init}
              color={INITIATIVE_COLORS[init]}
              isActive={filters.initiatives.length === 0 || filters.initiatives.includes(init)}
              onToggle={() => toggleFilter('initiatives', init, ALL_INITIATIVES, filters.initiatives, setFilters)}
            />
          ))}
        </div>
      </FilterSection>

      {/* ── Status ───────────────────────────────────── */}
      <FilterSection
        title="Status"
        sectionKey="status"
        isOpen={openSections.status}
        onToggle={toggleSection}
        activeCount={filters.statuses.length === 0 ? allStatuses.length : filters.statuses.length}
        totalCount={allStatuses.length}
        onAll={() => setFilters({ statuses: [] })}
        onNone={() => setFilters({ statuses: ['__none__' as Status] })}
      >
        <div className="flex flex-wrap gap-1">
          {allStatuses.map((status) => (
            <FilterChip
              key={status}
              label={status}
              color={STATUS_COLORS[status] ?? STATUS_DEFAULT_COLOR}
              isActive={filters.statuses.length === 0 || filters.statuses.includes(status)}
              onToggle={() => toggleFilter('statuses', status, allStatuses, filters.statuses, setFilters)}
            />
          ))}
        </div>
      </FilterSection>

      {/* ── Phase ────────────────────────────────────── */}
      <FilterSection
        title="Phase"
        sectionKey="phase"
        isOpen={openSections.phase}
        onToggle={toggleSection}
        activeCount={filters.phases.length === 0 ? allPhases.length : filters.phases.length}
        totalCount={allPhases.length}
        onAll={() => setFilters({ phases: [] })}
        onNone={() => setFilters({ phases: ['__none__' as Phase] })}
      >
        <div className="flex flex-wrap gap-1">
          {allPhases.map((phase) => (
            <FilterChip
              key={phase}
              label={phase}
              color={PHASE_COLORS[phase] ?? '#6b7280'}
              isActive={filters.phases.length === 0 || filters.phases.includes(phase)}
              onToggle={() => toggleFilter('phases', phase, allPhases, filters.phases, setFilters)}
            />
          ))}
        </div>
      </FilterSection>

      {/* ── Source ────────────────────────────────────── */}
      <FilterSection
        title="Source"
        sectionKey="source"
        isOpen={openSections.source}
        onToggle={toggleSection}
        activeCount={filters.sources.length === 0 ? ALL_SOURCES.length : filters.sources.length}
        totalCount={ALL_SOURCES.length}
        onAll={() => setFilters({ sources: [] })}
        onNone={() => setFilters({ sources: ['__none__' as DataSource] })}
      >
        <div className="flex flex-wrap gap-1">
          {ALL_SOURCES.map((source) => (
            <FilterChip
              key={source}
              label={SOURCE_LABELS[source]}
              color={SOURCE_COLORS[source]}
              isActive={filters.sources.length === 0 || filters.sources.includes(source)}
              onToggle={() => toggleFilter('sources', source, ALL_SOURCES, filters.sources, setFilters)}
            />
          ))}
        </div>
      </FilterSection>

      {/* ── Relationship Types ────────────────────────── */}
      <FilterSection
        title="Relationships"
        sectionKey="relationship"
        isOpen={openSections.relationship}
        onToggle={toggleSection}
        activeCount={filters.relationshipFilter.length === 0 ? ALL_REL_TYPES.length : filters.relationshipFilter.length}
        totalCount={ALL_REL_TYPES.length}
        onAll={() => setFilters({ relationshipFilter: [] })}
        onNone={() => setFilters({ relationshipFilter: ['__none__' as RelationshipType] })}
      >
        <div className="flex flex-wrap gap-1">
          {ALL_REL_TYPES.map((rel) => (
            <FilterChip
              key={rel}
              label={REL_TYPE_LABELS[rel]}
              color={REL_TYPE_COLORS[rel]}
              isActive={filters.relationshipFilter.length === 0 || filters.relationshipFilter.includes(rel)}
              onToggle={() => toggleFilter('relationshipFilter', rel, ALL_REL_TYPES, filters.relationshipFilter, setFilters)}
            />
          ))}
        </div>
      </FilterSection>

      {/* ── Reset link ───────────────────────────────── */}
      {hasActiveFilters && (
        <div className="px-3 pb-2 pt-1">
          <button
            onClick={resetFilters}
            className="text-[10px] transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#93c5fd'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            Reset all filters
          </button>
        </div>
      )}
    </div>
  );
}

// ── Toggle Helper ────────────────────────────────────────────────────

function toggleFilter<T>(
  filterKey: string,
  value: T,
  allValues: T[],
  currentValues: T[],
  setFilters: (filters: Record<string, unknown>) => void,
) {
  if (currentValues.length === 0) {
    // Currently all visible — clicking one means "show only NOT this one" → show all except
    const newValues = allValues.filter(v => v !== value);
    setFilters({ [filterKey]: newValues });
  } else if (currentValues.includes(value)) {
    // Currently active — deactivate it
    const newValues = currentValues.filter(v => v !== value);
    // If we'd show all, reset to empty (= all visible)
    setFilters({ [filterKey]: newValues.length === allValues.length ? [] : newValues });
  } else {
    // Currently inactive — activate it
    const newValues = [...currentValues, value];
    // If all are now selected, reset to empty
    setFilters({ [filterKey]: newValues.length === allValues.length ? [] : newValues });
  }
}

// ── Section Component ─────────────────────────────────────────────────

function FilterSection({
  title,
  sectionKey,
  isOpen,
  onToggle,
  activeCount,
  totalCount,
  onAll,
  onNone,
  children,
}: {
  title: string;
  sectionKey: string;
  isOpen: boolean;
  onToggle: (key: string) => void;
  activeCount: number;
  totalCount: number;
  onAll: () => void;
  onNone: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ borderBottom: '1px solid var(--glass-border)' }}>
      {/* Header */}
      <button
        onClick={() => onToggle(sectionKey)}
        className="w-full flex items-center justify-between px-3 py-2 transition-colors"
        style={{ color: 'var(--text-secondary)' }}
      >
        <span className="flex items-center gap-1.5 font-semibold text-[11px]">
          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {title}
        </span>
        <span className="flex items-center gap-2">
          <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-dim)' }}>
            {activeCount}/{totalCount}
          </span>
          <span className="flex gap-1 text-[10px]">
            <span
              onClick={(e) => { e.stopPropagation(); onAll(); }}
              className="cursor-pointer transition-colors"
              style={{ color: 'var(--text-dim)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#93c5fd'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-dim)'; }}
            >
              All
            </span>
            <span style={{ color: 'var(--text-dim)' }}>&middot;</span>
            <span
              onClick={(e) => { e.stopPropagation(); onNone(); }}
              className="cursor-pointer transition-colors"
              style={{ color: 'var(--text-dim)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#93c5fd'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-dim)'; }}
            >
              None
            </span>
          </span>
        </span>
      </button>

      {/* Content */}
      {isOpen && (
        <div className="px-3 pb-2.5">
          {children}
        </div>
      )}
    </div>
  );
}
